import { useState, useRef, useCallback } from 'react';
import { AreaConfig } from '#server/models';
import { useAdminSubjectDraft } from '@/contexts';
import { useDraftFileUpload } from '../hooks/useDraftFileUpload';
import { useUndoRedo, useUndoRedoKeyboard } from '../hooks/useUndoRedo';
import { ADMIN_SCROLLBAR_CLASSES } from '../../constants';
import { AdminContextPreview } from '../components/AdminContextPreview';
import { DraftFileField } from '../components/DraftFileField';
import { FieldTooltip } from '../components/FieldTooltip';
import { PreviewPanel } from '../components/PreviewPanel';
import { UndoRedoButtons } from '../components/UndoRedoButtons';
import { Vec3Input } from '../components/Vec3Input';
import { Switch } from '@/components/ui/switch';

// ─── Types ────────────────────────────────────────────────────────────────────

type Transform3D = {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
};

type ContextSnapshot = {
  referenceContext: Transform3D;
  subjectContext: Transform3D;
  fullContext: { rotation: [number, number, number] };
};

// ─── Shared transform input trio ──────────────────────────────────────────────

/** Position / rotation / scale Vec3Input trio, shared by reference and subject context sections. */
const ContextTransformInputs = ({
  value,
  onChange,
  tooltips,
  onAdjust,
  activeMode,
  onCommit,
}: {
  value: Transform3D;
  onChange: (patch: Partial<Transform3D>) => void;
  tooltips: { position: string; rotation: string; scale: string };
  onAdjust?: (mode: 'translate' | 'rotate' | 'scale') => void;
  activeMode?: 'translate' | 'rotate' | 'scale' | null;
  onCommit?: () => void;
}) => (
  <>
    <Vec3Input
      label="Position"
      tooltip={tooltips.position}
      value={value.position}
      onChange={(v) => onChange({ position: v })}
      onCommit={onCommit}
      onAdjust={onAdjust ? () => onAdjust('translate') : undefined}
      isAdjusting={activeMode === 'translate'}
    />
    <Vec3Input
      label="Rotation (degrees)"
      tooltip={tooltips.rotation}
      value={value.rotation}
      onChange={(v) => onChange({ rotation: v })}
      onCommit={onCommit}
      onAdjust={onAdjust ? () => onAdjust('rotate') : undefined}
      isAdjusting={activeMode === 'rotate'}
    />
    <Vec3Input
      label="Scale"
      tooltip={tooltips.scale}
      value={value.scale}
      onChange={(v) => onChange({ scale: v })}
      onCommit={onCommit}
      onAdjust={onAdjust ? () => onAdjust('scale') : undefined}
      isAdjusting={activeMode === 'scale'}
    />
  </>
);

// ─── Step ─────────────────────────────────────────────────────────────────────

interface Step3Props {
  areaConfig: AreaConfig;
}

export const Step3ContextModels = ({ areaConfig }: Step3Props) => {
  const { draft, updateConfig } = useAdminSubjectDraft();
  const { config3D } = draft;
  const [transformMode, setTransformMode] = useState<'translate' | 'rotate' | 'scale' | null>(null);
  const [transformTarget, setTransformTarget] = useState<'reference' | 'subject' | 'fullContext' | null>(null);
  const { uploadFbx } = useDraftFileUpload();
  const { push, undo, redo, canUndo, canRedo, reset } = useUndoRedo<ContextSnapshot>(50);

  const updateContext = (patch: Partial<typeof config3D.context>) => {
    updateConfig({ context: { ...config3D.context, ...patch } });
  };

  const snapshotContext = useCallback((): ContextSnapshot => ({
    referenceContext: { ...config3D.context.referenceContext },
    subjectContext: { ...config3D.context.subjectContext },
    fullContext: { rotation: [...config3D.context.fullContext.rotation] as [number, number, number] },
  }), [config3D.context]);

  const applySnapshot = useCallback((snap: ContextSnapshot) => {
    updateConfig({
      context: {
        ...config3D.context,
        referenceContext: snap.referenceContext,
        subjectContext: snap.subjectContext,
        fullContext: snap.fullContext,
      },
    });
  }, [config3D.context, updateConfig]);

  // Reset history only when going from fully inactive to any active state.
  // Switching between targets/modes does not reset — history is shared across all three targets.
  const prevActiveRef = useRef(false);
  const isActive = transformMode !== null || transformTarget !== null;
  if (isActive && !prevActiveRef.current) reset(snapshotContext());
  prevActiveRef.current = isActive;

  useUndoRedoKeyboard(undo, redo, applySnapshot);

  const pushCurrent = useCallback(() => push(snapshotContext()), [push, snapshotContext]);

  if (draft.type !== '3D') {
    return (
      <div className="text-[#666] text-sm">
        Context Models are only available for {areaConfig.subjectName.plural.toLowerCase()} with type "3D".
      </div>
    );
  }

  return (
    <div className="flex gap-6 h-full">
      {/* Left: form fields */}
      <div className={`flex-1 space-y-4 overflow-y-auto pr-2 max-h-[calc(100vh-280px)] ${ADMIN_SCROLLBAR_CLASSES}`}>
        <h2 className="text-base font-semibold text-[#E8E8E8]">Step 3 – Context Models</h2>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Reference Context</h3>
            <FieldTooltip text="A fixed anatomical reference model (e.g. full body or skeleton) shown as a green wireframe. It stays visible at all times to help the student orientate the subject within a larger structure. This model does not move independently — it rotates with the Full Context group." />
          </div>
          <DraftFileField
            label="Reference Context FBX"
            pathValue={config3D.referenceContextModelPath}
            onPathChange={(v) => updateConfig({ referenceContextModelPath: v })}
            blobUrl={config3D._referenceContextBlobUrl}
            onFileChange={(f) => uploadFbx('_referenceContextBlobUrl', f)}
          />
          <ContextTransformInputs
            value={config3D.context.referenceContext}
            onChange={(patch) => updateContext({ referenceContext: { ...config3D.context.referenceContext, ...patch } })}
            tooltips={{
              position: 'Offset of the reference context model from the scene origin. Use this to align it with the subject model.',
              rotation: 'Initial rotation of the reference context model in degrees. Adjust to match the orientation of your subject model.',
              scale: 'Size of the reference context model relative to the subject. Scale it so the subject fits naturally within the reference anatomy.',
            }}
            onAdjust={(mode) => {
              if (transformTarget === 'reference' && transformMode === mode) {
                setTransformMode(null);
                setTransformTarget(null);
              } else {
                setTransformTarget('reference');
                setTransformMode(mode);
              }
            }}
            activeMode={transformTarget === 'reference' ? transformMode : null}
            onCommit={pushCurrent}
          />
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-3">
            <Switch
              checked={config3D.context.subjectContextEnabled}
              onCheckedChange={(v) => updateContext({ subjectContextEnabled: v })}
            />
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">
                {areaConfig.subjectName.singular} Context
              </h3>
              <FieldTooltip text={`An optional secondary context model shown as a white wireframe (e.g. a regional outline around the subject). Unlike the reference context, this is only displayed when the toggle is on. Useful for highlighting the subject's immediate anatomical neighbourhood.`} />
            </div>
          </div>
          {config3D.context.subjectContextEnabled && (
            <div className="space-y-3 pl-3 border-l border-[#333]">
              <DraftFileField
                label="Context Model FBX"
                pathValue={config3D.contextModelPath}
                onPathChange={(v) => updateConfig({ contextModelPath: v })}
                blobUrl={config3D._contextBlobUrl}
                onFileChange={(f) => uploadFbx('_contextBlobUrl', f)}
              />
              <ContextTransformInputs
                value={config3D.context.subjectContext}
                onChange={(patch) => updateContext({ subjectContext: { ...config3D.context.subjectContext, ...patch } })}
                tooltips={{
                  position: 'Offset of the subject context model from the scene origin.',
                  rotation: 'Initial rotation of the subject context model in degrees.',
                  scale: 'Size of the subject context model relative to the subject.',
                }}
                onAdjust={(mode) => {
                  if (transformTarget === 'subject' && transformMode === mode) {
                    setTransformMode(null);
                    setTransformTarget(null);
                  } else {
                    setTransformTarget('subject');
                    setTransformMode(mode);
                  }
                }}
                activeMode={transformTarget === 'subject' ? transformMode : null}
                onCommit={pushCurrent}
              />
            </div>
          )}
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Full Context Rotation</h3>
            <FieldTooltip text="Base rotation applied to the entire context group (both reference and subject context models together) before the camera-tracking rotation is applied. Use this to align the context group's starting orientation with the subject." />
          </div>
          <Vec3Input
            label="Rotation (degrees)"
            tooltip="Initial rotation of the whole context group in degrees. The context group then continuously rotates to follow the camera azimuth and polar angle, keeping the anatomy reference correctly oriented as the user orbits."
            value={config3D.context.fullContext.rotation}
            onChange={(v) => updateContext({ fullContext: { rotation: v } })}
            onCommit={pushCurrent}
            onAdjust={() => {
              if (transformTarget === 'fullContext' && transformMode === 'rotate') {
                setTransformMode(null);
                setTransformTarget(null);
              } else {
                setTransformTarget('fullContext');
                setTransformMode('rotate');
              }
            }}
            isAdjusting={transformTarget === 'fullContext' && transformMode === 'rotate'}
          />
        </section>
      </div>

      {/* Right: 3D Preview */}
      <PreviewPanel
        headerAction={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              {(['reference', 'subject', 'fullContext'] as const).map((t) => {
                const subjectLabel = areaConfig.subjectName.singular
                  .split(' ')
                  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                  .join(' ');
                const label = t === 'reference' ? 'Ref' : t === 'subject' ? subjectLabel : 'Full Ctx';
                const disabled = t === 'subject' && (!config3D.context.subjectContextEnabled || !config3D._contextBlobUrl);
                return (
                  <button
                    key={t}
                    disabled={disabled}
                    onClick={() => {
                      setTransformTarget((prev) => (prev === t ? null : t));
                      setTransformMode(null);
                    }}
                    className={
                      disabled
                        ? 'px-2 py-0.5 text-xs rounded text-[#555] cursor-not-allowed'
                        : transformTarget === t
                        ? 'px-2 py-0.5 text-xs rounded bg-[#444] text-[#E8E8E8] font-semibold'
                        : 'px-2 py-0.5 text-xs rounded text-[#888] hover:text-[#CCC] hover:bg-[#2A2A2A]'
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {transformTarget && (
              <div className="flex items-center gap-1">
                {(transformTarget === 'fullContext' ? (['rotate'] as const) : (['translate', 'rotate', 'scale'] as const)).map((m) => (
                  <button
                    key={m}
                    onClick={() => setTransformMode((prev) => (prev === m ? null : m))}
                    className={
                      transformMode === m
                        ? 'px-2 py-0.5 text-xs rounded bg-[#C3D82E] text-[#1A1A1A] font-semibold'
                        : 'px-2 py-0.5 text-xs rounded text-[#888] hover:text-[#CCC] hover:bg-[#2A2A2A]'
                    }
                  >
                    {m === 'translate' ? 'Move' : m === 'rotate' ? 'Rotate' : 'Scale'}
                  </button>
                ))}
              </div>
            )}
          </div>
        }
        footer={
          <UndoRedoButtons
            canUndo={canUndo}
            onUndo={() => { const snap = undo(); if (snap) applySnapshot(snap); }}
            canRedo={canRedo}
            onRedo={() => { const snap = redo(); if (snap) applySnapshot(snap); }}
          />
        }
      >
        <AdminContextPreview
          draft={draft}
          transformMode={transformMode}
          transformTarget={transformTarget}
          onContextTransformChange={(target, t) => {
            const newReferenceContext = target === 'reference'
              ? { ...config3D.context.referenceContext, ...t }
              : config3D.context.referenceContext;
            const newSubjectContext = target === 'subject'
              ? { ...config3D.context.subjectContext, ...t }
              : config3D.context.subjectContext;
            const newFullContext = target === 'fullContext'
              ? { rotation: t.rotation }
              : config3D.context.fullContext;
            if (target === 'fullContext') updateContext({ fullContext: newFullContext });
            else if (target === 'reference') updateContext({ referenceContext: newReferenceContext });
            else updateContext({ subjectContext: newSubjectContext });
            push({ referenceContext: newReferenceContext, subjectContext: newSubjectContext, fullContext: newFullContext });
          }}
        />
      </PreviewPanel>
    </div>
  );
};
